import React, { useState } from "react";
import { SingleDatePicker } from "react-dates";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Input, InputContainer, Dropdown, Checkbox, RadioButtons } from "components/common";
import {
  addBatchName,
  updateScheduledTime,
  updateSelectedTimeType,
  updateCommunicationTypes,
  clearBatch,
  updateRecurringSchedule,
} from "state/reducers/communicationBatching";
import { useCommsBatching } from "utils";
import { useHistory } from "react-router";
import { getRecurringOptions } from './utils/recurringOptions';
import MarketingNavigation from "./utils/components/MarketingNavigation";
import MarketingStepper from "./utils/components/MarketingStepper";
import MarketingButtons from "./utils/components/MarketingButtons";

const SEND_NOW = "Now";
const SEND_LATER = "Future";
const TIMES = [SEND_NOW, SEND_LATER];

const parseTimeHoursAndMinutes = (timeString, setTimeValue) => {
  const time = moment(timeString);
  const hours = time.hours() < 10 ? `0${time.hours()}` : time.hours();
  const minutes = time.minutes() < 10 ? `0${time.minutes()}` : time.minutes();
  const timeHoursAndMinutes = `${hours}:${minutes}`;
  if (setTimeValue) { setTimeValue(timeHoursAndMinutes); }
  return timeHoursAndMinutes;
};

const BatchDetails = () => {
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const history = useHistory();
  const [recurringOption, setRecurringOption] = useState();

  const batchData = useSelector((state) => state.communicationBatching);

  const {
    batchName,
    communicationTypes,
    scheduledTime,
    selectedTimeType,
  } = batchData;

  let defaultTimeValue = "00:00";
  if (scheduledTime) {
    defaultTimeValue = parseTimeHoursAndMinutes(scheduledTime);
  }

  const [showCalendar, setShowCalendar] = useState(false);
  const [timeValue, setTimeValue] = useState(defaultTimeValue);
  const { getNextLink } = useCommsBatching({ currentPage: 'details' });

  const RECURRING_OPTIONS = getRecurringOptions(scheduledTime);

  const {
    email,
    text,
    push,
  } = communicationTypes;

  const atleastOneCommunicationType = !email && !text && !push;

  const handleInput = (e) => {
    setError("");
    const invalid = /\/|\s|\\/;
    if (invalid.test(e.target.value)) {
      setError("space and slash characters are not allowed");
    } else {
      dispatch(addBatchName(e.target.value));
    }
  };

  const handleDiscardChanges = () => {
    dispatch(clearBatch());
    history.push("/v2-marketing");
  };

  return (
    <div className="flex flex-col h-full">
      <MarketingNavigation/>
      <MarketingStepper />
      <MarketingButtons
        nextButton="Continue"
        nextLink={getNextLink()}
        required={!batchName || !scheduledTime || !selectedTimeType || (atleastOneCommunicationType)}
        spaceBetween
        discardChanges={handleDiscardChanges}
        discardButton="Discard"
        onNext={() => {
          dispatch(updateScheduledTime(moment(scheduledTime).utc().toString()));
          dispatch(updateRecurringSchedule(RECURRING_OPTIONS[recurringOption]));
        }}
      />
      <div>
        <p className="font-semibold">1. Add batch name</p>
        {error && <p className="text-red-600">{error}</p>}
        <InputContainer className="mt-2">
          <Input
            placeholder="Batch name"
            value={batchName || ""}
            onChange={handleInput}
            type="tel"
          />
        </InputContainer>
      </div>
      <div>
        <p className="font-semibold mt-8">3. Select Time</p>
        <RadioButtons
          elements={TIMES.map((item) => ({
            id: item,
            selected: item === selectedTimeType,
          }))}
          component={({ element }) => {
            return (
              <div className="flex flex-row" key={element.id}>
                <p>{element.id}</p>
                {element.id === SEND_LATER && element.selected && (
                  <>
                    <SingleDatePicker
                      numberOfMonths={1}
                      date={scheduledTime && moment(scheduledTime) || moment()}
                      onDateChange={(date) => {
                        const [hour, minutes] = timeValue.split(":");
                        const pickedTime = moment(date);
                        pickedTime.hour(hour);
                        pickedTime.minute(minutes);
                        dispatch(updateScheduledTime(moment(pickedTime).toString()));
                        setShowCalendar(false);
                      }}
                      focused={showCalendar}
                      onFocusChange={({ focused }) => setShowCalendar(focused)}
                      id="calendar-id"
                    />
                    <input
                      type="time"
                      id="appt"
                      name="appt"
                      required
                      value={timeValue}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onChange={(e) => {
                        setTimeValue(e.target.value);
                        const [hour, minutes] = e.target.value.split(":");
                        const pickedTime = moment(scheduledTime || moment());
                        pickedTime.hour(hour);
                        pickedTime.minute(minutes);
                        dispatch(updateScheduledTime(pickedTime.toString()));
                      }}
                    />
                  </>
                )}
              </div>);
          }}
          didSelect={(element) => {
            // whenever the reselect either now or later.
            const now = moment();
            dispatch(updateScheduledTime(now.toString()));
            dispatch(updateSelectedTimeType(element.id));
            parseTimeHoursAndMinutes(now.toString(), setTimeValue);
          }}
          nameRadioButton
        />

      </div>
      <div>
        <p className="font-semibold mt-8">2. Select Communication Types</p>
        {error && <p className="text-red-600">{error}</p>}
        <Checkbox
          label="Email"
          checked={!!email}
          onChange={() => dispatch(updateCommunicationTypes({
            ...communicationTypes,
            email: !email,
          }))}
        />
        <Checkbox
          label="Text"
          checked={!!text}
          onChange={() => dispatch(updateCommunicationTypes({
            ...communicationTypes,
            text: !text,
          }))}
        />
        <Checkbox
          label="Push"
          checked={!!push}
          onChange={() => dispatch(updateCommunicationTypes({
            ...communicationTypes,
            push: !push,
          }))}
        />
      </div>
      <div>
        <p className="font-semibold mt-8">2. Recurring </p>
        {error && <p className="text-red-600">{error}</p>}
        <Dropdown
          elements={RECURRING_OPTIONS}
          disable={scheduledTime}
          component={(e) => (e ? <div className="py-2">{e.name}</div> : <></>)}
          selectedElement={ recurringOption? RECURRING_OPTIONS[recurringOption] : RECURRING_OPTIONS[0] }
          didSelect={(v) => {
            const idx = RECURRING_OPTIONS.findIndex((o) => o.name === v.name);
            setRecurringOption(idx);
            dispatch(updateRecurringSchedule(v.value));
          }}
        />
      </div>
    </div>
  );
};

export default BatchDetails;
